import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import 'moment/locale/es';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import {
  ActivatedRoute,
  Event,
  NavigationEnd,
  PRIMARY_OUTLET,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
  Router
} from '@angular/router';
import { DateTimeAdapter, OwlDateTimeIntl, OWL_DATE_TIME_FORMATS } from '@danielmoncada/angular-datetime-picker';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DEFAULT_LANG } from './core/config/constants';
import { createDateFormats } from './core/factories/date-formats.factory';
import { createDateTranslations } from './core/factories/date-translations.factory';
import { PendingService } from './core/services/base/pending.service';
import { getPollLanguage, setSession } from './core/services/storage.service';
import { TranslationService } from './core/services/translation.service';
import { TitlesService } from './core/services/visual/titles.service';

const getDataAttribute = (route: ActivatedRoute, attributeName: string) => {
  const children: ActivatedRoute[] = route.children;
  for (const child of children) {
    if (child.outlet !== PRIMARY_OUTLET) {
      continue;
    }

    const attribute = getDataAttribute(child, attributeName);
    if (attribute) {
      return attribute;
    }
  }

  return route.snapshot.data[attributeName];
};

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  providers: [
    { provide: OwlDateTimeIntl, useFactory: createDateTranslations, deps: [TranslationService] },
    { provide: OWL_DATE_TIME_FORMATS, useFactory: createDateFormats, deps: [TranslationService] },
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  private subscription: Subscription;

  @HostListener('window:message', ['$event'])
  onMessage(e) {
    if (e.data.token !== undefined) {
      const session = { token: e.data.token, refreshToken: e.data.refreshToken };
      setSession(session);
    }
  }

  constructor(
    private router: Router,
    private pendingService: PendingService,
    private translationService: TranslationService,
    private activeRoute: ActivatedRoute,
    private titlesService: TitlesService,
    private dateTimeAdapter: DateTimeAdapter<any>,
  ) {
  }

  ngOnInit() {
    this.initTranslations();
    this.initRouterEvents();
    registerLocaleData(localeEs);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private initTranslations = () => {
    const lang = getPollLanguage();
    this.translationService.changeDefaultAppLanguage(DEFAULT_LANG);
    this.translationService.changeAppLanguage(lang);
    this.dateTimeAdapter.setLocale(DEFAULT_LANG);
  }

  private initRouterEvents = () => {
    this.subscription = this.router.events
      .pipe(
        filter(
          (event: Event) => (event instanceof RouteConfigLoadStart
            || event instanceof RouteConfigLoadEnd
            || event instanceof NavigationEnd
          )
        )
      )
      .subscribe((event: Event) => {
        if (event instanceof RouteConfigLoadStart) {
          this.pendingService.addBlockingPending();
        } else if (event instanceof RouteConfigLoadEnd) {
          this.pendingService.removeBlockingPending();
        } else {
          const title = getDataAttribute(this.activeRoute.root, 'title');
          if (title) {
            this.titlesService.setTranslatingTitle(title);
          }
          window.scrollTo(0, 0);
        }
      });
  }
}
